import React, { useCallback } from "react";
import {
  BlockContentValue,
  PartialSanityImageType
} from "@silversea-ssc/ui-components-react";
import { getCookie, setCookie } from "src/utils/cookies";
import { isEnvFeatureEnabled } from "src/utils/feature-flags";
import { stringPresent } from "src/utils/strings";
import { FormDisclaimerFragment } from "types/graphql-types";
import { INavLink } from "components/HeaderNavBar/type";
const SubscribeToNewsletterPopup = React.lazy(
  () => import("./SubscribeToNewsletterPopup")
);
import { isApproachGuidesVersion } from "src/utils/approach-guides";
import { useLocation } from "@gatsbyjs/reach-router";
import { blockContentToString } from "src/gatsby/algolia/blockContentToString";
import { useCustomPromo } from "components/CustomPromo/hooks";
import { SubscribeFormContent } from "components/SubscribeToNewsletter/types";

type Props = {
  backgroundImage: PartialSanityImageType | undefined;
  subscribeFormContent: SubscribeFormContent;
  formDisclaimerContent: FormDisclaimerFragment | undefined;
  privacyPolicyUrl: INavLink;
  offerPageUrl: string | undefined;
  cookieName?: string;
  promoConfigurationCode?: string;
};

type CookieStatusValue = "closed" | "submitted";

const MaybeSubscribeToNewsletterPopup: React.FC<Props> = (props) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const location = useLocation();
  const { promoIsValid } = useCustomPromo();
  const skipOpen = isApproachGuidesVersion(location.search) || promoIsValid;

  const onRequestClose = useCallback(() => {
    const currentStatus = getStatusFromCookie(props.cookieName);
    if (currentStatus !== "submitted") {
      setStatusToCookie(
        "closed",
        60 * 60 * 24 * 15 /* 15 days */,
        props.cookieName
      );
    }
    setIsModalOpen(false);
  }, [props.cookieName]);

  const onFormSubmitted = useCallback(() => {
    setStatusToCookie(
      "submitted",
      60 * 60 * 24 * 365 /* 1 year */,
      props.cookieName
    );
  }, [props.cookieName]);

  const cookie = getStatusFromCookie(props.cookieName);
  const isClosed = cookie === "closed" || cookie === "submitted";
  const isActive = isEnvFeatureEnabled(process.env.GATSBY_ENABLE_SFO_POPUP);

  React.useEffect(() => {
    if (isClosed || !isActive || skipOpen) return;

    setTimeout(() => {
      setIsModalOpen(true);
    }, 3000);
  }, [setIsModalOpen, isClosed, isActive, skipOpen]);

  if (!isModalOpen) return null;
  if (
    !stringPresent(
      blockContentToString(props.subscribeFormContent.headingTitle)
    )
  )
    return null;
  return (
    <React.Suspense>
      <SubscribeToNewsletterPopup
        {...props}
        onRequestClose={onRequestClose}
        onFormSubmitted={onFormSubmitted}
        isOpen={true}
        privacyPolicyUrl={props.privacyPolicyUrl}
      />
    </React.Suspense>
  );
};

const setStatusToCookie = (
  status: CookieStatusValue,
  maxAge: number,
  cookieName?: string
) => {
  setCookie(cookieName ?? "subscribe_to_newsletter_popup", status, {
    maxAge
  });
};

const getStatusFromCookie = (
  cookieName?: string
): CookieStatusValue | undefined => {
  return getCookie(
    cookieName ?? "subscribe_to_newsletter_popup"
  ) as CookieStatusValue;
};

export default MaybeSubscribeToNewsletterPopup;
